import { CommonModule } from "@angular/common";
import { Component, computed, EventEmitter, Input, OnDestroy, OnInit, Output, Signal, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { NbButtonModule, NbInputModule, NbDatepickerModule, NbIconModule, NbTooltipModule, NbPopoverModule, NbPopoverDirective, NbPopoverComponent } from "@nebular/theme";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { StickinessService } from "src/app/core/services/stickiness.service";
import { TimeUnit } from "src/app/shared/models/time.model";
import { TimePickerPayload, RelativeTimeRange } from "./time-picker.models";
import { formatDateTime } from 'src/app/shared/operators/time-operator';
import moment from "moment";
import "moment-timezone";

@Component({
  selector: "app-time-picker",
  templateUrl: "./time-picker.component.html",
  styleUrls: ["./time-picker.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbButtonModule,
    NbInputModule,
    NbDatepickerModule,
    NbIconModule,
    NbTooltipModule,
    TranslateModule,
    NbPopoverModule,
  ],
  providers: [StickinessService, TranslateService],
})
export class TimePickerComponent implements OnInit, OnDestroy {
  @Input() interval: number = 3;
  @Input() timeUnit: TimeUnit = TimeUnit.HOURS;
  @Input() date: Date = new Date();
  @Input() useUserPreferences: boolean = true;
  @Input() timezone: string = moment.tz.guess();
  @Output() timeRangeSelected = new EventEmitter<TimePickerPayload>();
  querySubscription: any;
  dateFrom: Date;
  dateTo: Date = new Date();
  now = new Date();
  selectedRelativeRange: RelativeTimeRange;
  timeRangeLabel: string = this.formatDisplayString();
  isPopoverOpen = false;

  @ViewChild(NbPopoverDirective) popover: NbPopoverDirective;

  readonly relativeRanges: RelativeTimeRange[] = this.getRelativeTimeRanges([
    { interval: 3, timeUnit: TimeUnit.HOURS },
    { interval: 6, timeUnit: TimeUnit.HOURS },
    { interval: 1, timeUnit: TimeUnit.DAYS },
    { interval: 2, timeUnit: TimeUnit.DAYS },
    { interval: 7, timeUnit: TimeUnit.DAYS },
    { interval: 14, timeUnit: TimeUnit.DAYS },
    { interval: 30, timeUnit: TimeUnit.DAYS },
  ]);

  constructor(
    private stickinessService: StickinessService,
    private route: ActivatedRoute,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.querySubscription = this.route.queryParams.subscribe((params) => {
      if (this.useUserPreferences) {
        if (this.restoreFromParams(parseInt(params["timeBack"]), params["timeUnit"], params["date"])) return;

        const saved = this.stickinessService.restoreTimeRangePickerState();
        this.restoreFromSavedState(saved || this.relativeRanges[0]);
      } else {
        this.restoreFromParams(this.interval, this.timeUnit, this.date);
      }
    });
  }

  private restoreFromParams(timeBack, timeUnit, date): boolean {
    this.interval = timeBack;

    if (this.interval && timeUnit) {
      const match = this.relativeRanges.find(
        (r) => r.interval === this.interval && r.timeUnit === timeUnit
      );

      if (match) {
        this.selectRelativeRange(match);
      } else {
        this.dateTo = date ? new Date(date) : new Date();
        this.dateFrom = this.calculateDateFrom();
        this.selectAbsoluteRange();
      }

      return true;
    }

    return false;
  }

  private restoreFromSavedState(saved: any) {
    if ("label" in saved) {
      this.selectRelativeRange(saved);
    } else {
      this.dateFrom = new Date(saved.dateFrom);
      this.dateTo = new Date(saved.dateTo);
      this.selectAbsoluteRange();
    }
  }

  selectRelativeRange(range: RelativeTimeRange) {
    this.interval = range.interval;
    this.timeUnit = range.timeUnit;
    this.date = new Date();
    this.selectedRelativeRange = range;
    this.dateFrom = null;
    this.stickinessService.saveTimeRangePickerState(range);
    this.updateSelectedRange();
  }

  selectAbsoluteRange() {
    this.interval = this.calculateDifferenceInMinutes();
    this.timeUnit = TimeUnit.MINUTES;
    this.date = this.dateTo;
    this.selectedRelativeRange = null;

    this.stickinessService.saveTimeRangePickerState({
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
    });

    this.updateSelectedRange();
  }

  openPopover() {
    this.popover?.show();
  }

  closePopover() {
    this.popover?.hide();
  }

  private calculateDifferenceInMinutes(): number {
    const from = moment(this.dateFrom).tz(this.timezone);
    const to = moment(this.dateTo).tz(this.timezone);
    return to.diff(from, "minutes");
  }

  private calculateDateFrom(): Date {
    const timeUnit: any = this.timeUnit.toLocaleLowerCase();
    return moment(this.dateTo || this.date)
      .tz(this.timezone)
      .subtract(this.interval, timeUnit)
      .toDate();
  }

  private updateSelectedRange(): void {
    const dateToInTimezone = moment(this.date).tz(this.timezone).toDate();
    const dateFrom = this.dateFrom ? this.dateFrom : this.calculateDateFrom();

    this.timeRangeLabel = this.formatDisplayString();
    this.closePopover();

    this.timeRangeSelected.emit({
      interval: this.interval,
      timeUnit: this.timeUnit,
      dateFrom: moment(dateFrom).tz(this.timezone).toDate(),
      dateTo: dateToInTimezone,
    });
  }

  private formatDisplayString(): string {
    if (this.selectedRelativeRange) {
      return this.selectedRelativeRange.label;
    } else if (this.dateFrom && this.dateTo) {
      const fromStr = formatDateTime(moment(this.dateFrom).tz(this.timezone), true);
      const toStr = formatDateTime(moment(this.dateTo).tz(this.timezone), true);
      return `${fromStr} → ${toStr}`.toLowerCase();
    }
  }

  private getRelativeTimeRanges(ranges: { interval: number, timeUnit: TimeUnit }[]): RelativeTimeRange[] {
    return ranges.map(range => ({
      label: `${this.translate.instant('data.COMMON.LAST')} ${range.interval} ${range.timeUnit.toLowerCase()}`,
      value: `${range.interval}${range.timeUnit[0].toLowerCase()}`,
      interval: range.interval,
      timeUnit: range.timeUnit
    }));
  }

  ngOnDestroy(): void {
    this.querySubscription?.unsubscribe();
  }
}
